// Shareable member-card tokens (must stay in sync with beat/internal/fanbase/shareable_token.go).
// Tokens do not expire. Payload is phone|marker (marker is "0" for new tokens; older links may use a timestamp).

const STABLE_MARKER = '0';

const checksumOf = (encoded) =>
    encoded.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 1000;

/**
 * Encodes a phone number into a shareable token (non-expiring, stable for the same phone).
 * @param {string} phoneNumber
 * @returns {string}
 */
export const encodePhoneNumberToToken = (phoneNumber) => {
    try {
        const data = `${phoneNumber}|${STABLE_MARKER}`;
        const encoded = btoa(data);
        return `${encoded}.${checksumOf(encoded).toString().padStart(3, '0')}`;
    } catch (error) {
        console.error('Error encoding phone number:', error);
        throw new Error('Failed to encode phone number');
    }
};

/**
 * Decodes a token back to a phone number. Never expires.
 * Accepts legacy timestamped tokens and tokens where "+" became a space in the query string.
 * @param {string} token
 * @returns {string|null}
 */
export const decodeTokenToPhoneNumber = (token) => {
    try {
        if (!token || typeof token !== 'string') {
            return null;
        }

        const parts = token.split('.');
        if (parts.length !== 2) {
            return null;
        }

        let [encoded, checksum] = parts;
        // application/x-www-form-urlencoded turns "+" into space
        encoded = encoded.replace(/ /g, '+');

        if (parseInt(checksum, 10) !== checksumOf(encoded)) {
            return null;
        }

        const decoded = atob(encoded);
        const dataParts = decoded.split('|');
        if (dataParts.length < 2) {
            return null;
        }

        const phoneNumber = dataParts[0];
        if (!phoneNumber || phoneNumber.length < 7) {
            return null;
        }

        return phoneNumber;
    } catch (error) {
        console.error('Error decoding token:', error);
        return null;
    }
};

/**
 * @param {string} token
 * @returns {boolean}
 */
export const isValidToken = (token) => {
    try {
        return decodeTokenToPhoneNumber(token) !== null;
    } catch {
        return false;
    }
};

/**
 * Creates a shareable URL with an encoded (non-expiring) phone token.
 * @param {string} baseUrl
 * @param {string} phoneNumber
 * @returns {string}
 */
export const createShareableUrl = (baseUrl, phoneNumber) => {
    const token = encodePhoneNumberToToken(phoneNumber);
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}token=${encodeURIComponent(token)}`;
};

/**
 * @param {string} url
 * @returns {string|null}
 */
export const extractPhoneNumberFromUrl = (url) => {
    try {
        const urlObj = new URL(url);
        const token = urlObj.searchParams.get('token');
        if (!token) {
            return null;
        }
        return decodeTokenToPhoneNumber(token);
    } catch (error) {
        console.error('Error extracting phone number from URL:', error);
        return null;
    }
};
